import { appendTicketMessage } from './technicalTickets';
import { buildProgramStepLink } from './programStepLinks';
import {
  normalizeConsultationType,
  buildConsultationReminderMessage,
  buildConsultationReminderTitle,
} from '../../functions/shared/consultationReminder';

const STEP_DISPLAY_NAMES = {
  questionario: 'Questionario iniziale',
  consegna: 'Consegna',
  consulenza: 'Consulenza',
  feedback_programmi: 'Feedback programma',
  check_finale: 'Check finale',
};

const dateFormatter = new Intl.DateTimeFormat('it-IT', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value?.toDate === 'function') {
    try {
      const parsed = value.toDate();
      return Number.isNaN(parsed?.getTime?.()) ? null : parsed;
    } catch {
      return null;
    }
  }
  return null;
}

function formatDate(value) {
  const d = toDate(value);
  return d ? dateFormatter.format(d) : '';
}

function formatDateTime(value) {
  return formatDate(value);
}

function buildProgramLabel({ programTitle, productName, sequenceIndex, sequenceTotal }) {
  const parts = [];
  const title = (programTitle || '').trim();
  const product = (productName || '').trim();
  const normalize = (value) => (value || '').toLowerCase().replace(/[^a-z0-9]/gi, '');

  if (title) parts.push(`“${title}”`);

  const hasSequence = Number.isFinite(sequenceIndex) && sequenceIndex > 0;
  if (hasSequence) {
    const hasTotal = Number.isFinite(sequenceTotal) && sequenceTotal > 0;
    const seqLabel = hasTotal ? `${sequenceIndex}/${sequenceTotal}` : `${sequenceIndex}`;
    const seqAlreadyInTitle = title.replace(/\s+/g, '').includes(seqLabel.replace(/\s+/g, ''));
    if (!seqAlreadyInTitle) {
      parts.push(hasTotal ? `#${sequenceIndex}/${sequenceTotal}` : `#${sequenceIndex}`);
    }
  }

  const productNormalized = normalize(product);
  const titleNormalized = normalize(title);
  const productAlreadyInTitle = productNormalized && titleNormalized.includes(productNormalized);
  if (product && !productAlreadyInTitle && product.toLowerCase() !== title.toLowerCase()) {
    parts.push(product);
  }

  if (!parts.length) return '';
  return parts.join(' · ');
}

function capitalize(value) {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function baseProgramSegment(label, { prepend = 'del', includeArticle = true } = {}) {
  if (!label) return '';
  const article = includeArticle ? ' programma ' : ' ';
  return ` ${prepend}${article}${label}`;
}

function buildStepDisplayName(stepKey, step = {}) {
  if (stepKey === 'consulenza') {
    const type = capitalize(normalizeConsultationType(step));
    return type ? `Consulenza ${type}` : 'Consulenza';
  }
  const fallback =
    step?.name ||
    STEP_DISPLAY_NAMES[stepKey] ||
    'Step programma';
  return fallback;
}

export const STEP_NOTIFICATION_TEMPLATES = {
  questionario: {
    title: 'Promemoria questionario iniziale',
    buildLines: (context) => {
      const { plannedLabel, programLabel, stepLink } = context;
      const lines = [
        `Ciao! Ti ricordiamo di completare il questionario iniziale${baseProgramSegment(programLabel)}.`,
      ];
      if (plannedLabel) {
        lines.push(`Lo step è previsto per ${plannedLabel}.`);
      }
      if (stepLink) {
        lines.push(`👉 [Clicca qui per completarlo](${stepLink})`);
        lines.push('Oppure accedi alla tua area personale, sezione "Programmi", e apri lo step "Questionario iniziale".');
      } else {
        lines.push(
          'Accedi alla tua area personale, sezione "Programmi", e apri lo step "Questionario iniziale".'
        );
      }
      return lines;
    },
  },
  consegna: {
    title: 'Promemoria consegna programma',
    buildLines: (context) => {
      const { plannedLabel, programLabel } = context;
      const lines = [
        `Ciao! È il momento di seguire la consegna${baseProgramSegment(programLabel)}.`,
      ];
      if (plannedLabel) {
        lines.push(`Troverai tutto il materiale disponibile a partire dal ${plannedLabel}.`);
      }
      lines.push(
        'Apri la sezione "Programmi" e segui lo step "Consegna" per procedere con il percorso.'
      );
      return lines;
    },
  },
  consulenza: {
    title: 'Promemoria consulenza',
    buildLines: ({ step }) => [buildConsultationReminderMessage(step)],
  },
  feedback_programmi: {
    title: 'Richiesta feedback programma',
    buildLines: (context) => {
      const { programLabel } = context;
      const lines = [
        `Hai qualche minuto per lasciarci un feedback${baseProgramSegment(programLabel)}?`,
        'Apri la sezione "Programmi" e completa lo step "Feedback" per farci sapere come sta andando.',
      ];
      return lines;
    },
  },
  check_finale: {
    title: 'Promemoria check finale',
    buildLines: (context) => {
      const { programLabel, stepLink } = context;
      const lines = [
        `È arrivato il momento del check ${baseProgramSegment(programLabel)}.`,
      ];
      lines.push('Ti aspettiamo entro la prossima settimana.');
      if (stepLink) {
        lines.push(`👉 [Clicca qui per completarlo](${stepLink})`);
        lines.push('Oppure apri la sezione "Programmi" e completa lo step "Check" per aggiornare il team.');
      } else {
        lines.push(
          'Apri la sezione "Programmi" e completa lo step "Check" per aggiornare il team.'
        );
      }
      return lines;
    },
  },
  default: {
    title: 'Promemoria step programma',
    buildLines: (context) => {
      const { stepDisplayName, programLabel } = context;
      return [
        `Ciao! Ti ricordiamo di completare lo step "${stepDisplayName}"${baseProgramSegment(
          programLabel
        )}.`,
      ];
    },
  },
};

function getTemplate(stepKey) {
  return STEP_NOTIFICATION_TEMPLATES[stepKey] || STEP_NOTIFICATION_TEMPLATES.default;
}

function joinLines(lines = []) {
  return lines
    .map((line) => (line || '').trim())
    .filter(Boolean)
    .join('\n\n')
    .trim();
}

export function normalizeProgramTitle(rawTitle) {
  if (!rawTitle) return 'Programma';
  return rawTitle
    .replace(/Percorsi/g, 'Programmi')
    .replace(/percorsi/g, 'programmi')
    .replace(/Percorso/g, 'Programma')
    .replace(/percorso/g, 'programma');
}

/**
 * Restituisce titolo, messaggio e metadati per una notifica legata agli step percorso.
 * @param {Object} context
 * @param {string} context.stepKey Chiave logica dello step (questionario, consegna, consulenza, feedback_programmi, check_finale).
 * @param {Object} [context.step] Step originale da Firestore.
 * @param {string} [context.programTitle] Titolo normalizzato del programma.
 * @param {string} [context.productName] Nome del prodotto associato.
 * @param {number} [context.sequenceIndex] Indice del programma nel gruppo.
 * @param {number} [context.sequenceTotal] Numero totale dei programmi del gruppo.
 * @param {string|Date} [context.plannedAt] Data prevista principale.
 * @returns {{title: string, message: string, ticketExtra: Object, conversationExtra: Object, toastMessage: string, programLabel: string, stepDisplayName: string}}
 */
export function buildStepNotificationPayload(context = {}) {
  const {
    stepKey,
    step = {},
    programTitle = '',
    productName = '',
    sequenceIndex = null,
    sequenceTotal = null,
    plannedAt = step?.plannedAt || null,
    stepLink = null,
  } = context;

  if (!stepKey) return null;

  const seqIndex = Number(sequenceIndex);
  const seqTotal = Number(sequenceTotal);

  const template = getTemplate(stepKey);
  const programLabel = buildProgramLabel({
    programTitle,
    productName,
    sequenceIndex: Number.isFinite(seqIndex) && seqIndex > 0 ? seqIndex : null,
    sequenceTotal: Number.isFinite(seqTotal) && seqTotal > 0 ? seqTotal : null,
  });
  const stepDisplayName = buildStepDisplayName(stepKey, step);
  const plannedLabel = formatDateTime(plannedAt);
  const consulenzaType = stepKey === 'consulenza' ? capitalize(normalizeConsultationType(step)) : '';

  const lines = template.buildLines({
    stepKey,
    step,
    programLabel,
    plannedLabel,
    stepDisplayName,
    consulenzaType,
    stepLink,
  });
  const message = joinLines(lines);
  const title = stepKey === 'consulenza' ? buildConsultationReminderTitle(step) : template.title;

  if (!message) return null;

  return {
    title,
    message,
    ticketExtra: {
      stepKey,
      stepName: step?.name || stepDisplayName,
      programTitle: programTitle || null,
      productName: productName || null,
      programSequenceIndex: Number.isFinite(sequenceIndex) ? sequenceIndex : null,
      programSequenceTotal: Number.isFinite(sequenceTotal) ? sequenceTotal : null,
      plannedAt: plannedAt || null,
      consulenzaType: consulenzaType || null,
    },
    conversationExtra: {
      stepKey,
      stepName: step?.name || stepDisplayName,
      programTitle: programTitle || null,
      consulenzaType: consulenzaType || null,
    },
    toastMessage: `${title.replace('Promemoria', 'Notifica')}: ${stepDisplayName}`,
    programLabel,
    stepDisplayName,
  };
}

export async function dispatchStepNotification({
  db,
  userUid,
  programId = null,
  stepKey,
  step = {},
  stepIndex = null,
  programTitle = '',
  productName = '',
  sequenceIndex = null,
  sequenceTotal = null,
  plannedAt = null,
  notifyUser = true,
  preserveOpenStatus = true,
  awaitNotification = false,
  ticketExtra = {},
  conversationExtra = {},
} = {}) {
  if (!db || !userUid || !stepKey) return null;

  const normalizedStepIndex = Number.isFinite(stepIndex) ? Number(stepIndex) : null;
  const stepLink = buildProgramStepLink({
    stepKey,
    userUid,
    percorsoId: programId,
    stepIndex: normalizedStepIndex,
    questionarioDocId: step?.questionarioDocId || step?.questionnaire?.questionarioUid,
    questionarioUid: step?.questionnaire?.questionarioUid,
    questionarioKey: step?.questionnaire?.questionarioKey,
  });

  const payload = buildStepNotificationPayload({
    stepKey,
    step,
    programTitle,
    productName,
    sequenceIndex,
    sequenceTotal,
    plannedAt,
    stepLink,
  });

  if (!payload?.message) return null;

  const triggeredAt = new Date().toISOString();

  const baseTicketExtra = {
    programId: programId || null,
    programLabel: payload.programLabel || null,
    stepIndex: normalizedStepIndex,
    source: 'program-step-notification',
    triggeredAt,
    tipoTicket: 'Tecnico',
  };

  const baseConversationExtra = {
    programId: programId || null,
    programLabel: payload.programLabel || null,
    stepIndex: normalizedStepIndex,
    source: 'program-step-notification',
    triggeredAt,
  };

  await appendTicketMessage({
    db,
    userUid,
    message: payload.message,
    title: payload.title,
    ticketExtra: {
      ...payload.ticketExtra,
      ...baseTicketExtra,
      ...ticketExtra,
    },
    conversationExtra: {
      ...payload.conversationExtra,
      ...baseConversationExtra,
      ...conversationExtra,
    },
    preserveOpenStatus,
    notifyUser,
    awaitNotification,
  });

  return { ...payload, triggeredAt };
}

export async function dispatchNeedsUpdateNotification({
  db,
  userUid,
  programId = null,
  stepKey,
  step = {},
  stepIndex = null,
  programTitle = '',
  productName = '',
  sequenceIndex = null,
  sequenceTotal = null,
  adminNote = '',
  notifyUser = true,
  preserveOpenStatus = true,
  awaitNotification = false,
  ticketExtra = {},
  conversationExtra = {},
} = {}) {
  if (!db || !userUid || !stepKey) return null;

  const stepDisplayName = buildStepDisplayName(stepKey, step);
  const programLabel = buildProgramLabel({
    programTitle,
    productName,
    sequenceIndex: Number.isFinite(sequenceIndex) ? sequenceIndex : null,
    sequenceTotal: Number.isFinite(sequenceTotal) ? sequenceTotal : null,
  });

  const lines = [];
  const baseIntro = `Abbiamo visto che hai compilato lo step "${stepDisplayName}"`;
  lines.push(programLabel ? `${baseIntro}${baseProgramSegment(programLabel)}` : `${baseIntro}.`);

  if (adminNote) {
    lines.push(`Ti chiediamo di aggiornarlo con queste indicazioni: ${adminNote}`);
  } else {
    lines.push('Ti chiediamo di aprirlo di nuovo e completare gli aggiornamenti richiesti.');
  }

  lines.push(`Riapri la tua area Programmi e aggiorna lo step "${stepDisplayName}".`);
  const message = joinLines(lines);
  if (!message) return null;

  const title = `Aggiornamento ${stepDisplayName}`;
  const triggeredAt = new Date().toISOString();
  const normalizedStepIndex = Number.isFinite(stepIndex) ? Number(stepIndex) : null;

  await appendTicketMessage({
    db,
    userUid,
    message,
    title,
    ticketExtra: {
      stepKey,
      stepName: step?.name || stepDisplayName,
      programTitle: programTitle || null,
      productName: productName || null,
      programSequenceIndex: Number.isFinite(sequenceIndex) ? sequenceIndex : null,
      programSequenceTotal: Number.isFinite(sequenceTotal) ? sequenceTotal : null,
      plannedAt: step?.plannedAt || null,
      adminNote: adminNote || null,
      programId: programId || null,
      programLabel,
      stepIndex: normalizedStepIndex,
      source: 'program-step-needs-update',
      triggeredAt,
      tipoTicket: 'Tecnico',
      ...ticketExtra,
    },
    conversationExtra: {
      stepKey,
      stepName: step?.name || stepDisplayName,
      programTitle: programTitle || null,
      programId: programId || null,
      programLabel,
      stepIndex: normalizedStepIndex,
      source: 'program-step-needs-update',
      triggeredAt,
      adminNote: adminNote || null,
      ...conversationExtra,
    },
    preserveOpenStatus,
    notifyUser,
    awaitNotification,
  });

  return {
    message,
    title,
    programLabel,
    stepDisplayName,
    triggeredAt,
    toastMessage: `Aggiornamento richiesto per ${stepDisplayName}.`,
  };
}
